import clsx from 'clsx'
import React, { FC, useRef, useState } from 'react'
import { useClickAway } from 'react-use'

import { GroupedReaction } from '@/types/TicketCommentReaction'

import { formatReactorNames, getEmojiDisplay } from './reactionUtils'

interface ReactionBadgeProps {
  canRemove?: boolean
  onRemove?: () => void
  reaction: GroupedReaction
}

/**
 * Displays a single emoji reaction with count and tooltip showing reactor names.
 * Clicking the badge toggles the tooltip visibility.
 */
export const ReactionBadge: FC<ReactionBadgeProps> = ({ reaction, onRemove, canRemove = false }) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const tooltipRef = useRef<HTMLDivElement>(null)

  useClickAway(tooltipRef, () => setShowTooltip(false))

  const handleClick = () => {
    setShowTooltip(!showTooltip)
  }

  const emojiDisplay = getEmojiDisplay(reaction.emoji)
  const tooltipText = formatReactorNames(reaction.reactors)

  return (
    <div className="relative inline-block" ref={tooltipRef}>
      <button
        aria-label={`${emojiDisplay} reaction by ${tooltipText}`}
        className={clsx(
          'inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm',
          'bg-emphasis hover:bg-action-secondary transition-colors',
          reaction.hasCurrentUserReacted && 'bg-inline-info ring-1 ring-action-primary',
        )}
        onClick={handleClick}
        type="button"
      >
        <span aria-label={reaction.emoji} className="text-base" role="img">
          {emojiDisplay}
        </span>
        <span className="font-medium text-tertiary">{reaction.count}</span>
      </button>

      {showTooltip && (
        <div
          className={clsx(
            'absolute bottom-full left-1/2 -translate-x-1/2 mb-2',
            'bg-primary text-primary px-3 py-2 rounded-lg shadow-lg',
            'text-sm whitespace-nowrap z-50',
            'border border-secondary',
          )}
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="text-base font-medium">{emojiDisplay}</span>
            <span className="text-tertiary">{tooltipText}</span>
          </div>
          {canRemove && reaction.hasCurrentUserReacted && onRemove && (
            <button
              className="mt-2 text-xs text-action-primary hover:underline"
              onClick={(e) => {
                e.stopPropagation()
                onRemove()
              }}
            >
              Remove your reaction
            </button>
          )}
          <div
            className="absolute top-full left-1/2 -translate-x-1/2 -mt-1"
            style={{
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '6px solid #E7E6E4',
            }}
          />
        </div>
      )}
    </div>
  )
}
